/**
 * Phase 5G-A7 — Biometric Mappings admin page.
 *
 * One table showing every employee with a biometric_id_norm, their
 * device-attribution stats over the last 60 days, and 3 actionable buttons:
 *   - Re-map (change device ID + retro-relink past punches)
 *   - Clean / heal (re-run remap with current ID to revert wrong attributions)
 *   - Unassign
 *
 * Solves: "punches up to ID 15 match correct employee, then start matching
 * the wrong one". Admin spots `wrong_attribution_ids` in red, hits Clean,
 * and the wrong rows are reverted to unmatched + the correct ones stay.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Fingerprint, AlertTriangle, RefreshCw, Search, ShieldCheck, Wand2,
  Link2, Link2Off, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

export default function BiometricMappings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [onlyMapped, setOnlyMapped] = useState(true);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [duplicatesCount, setDuplicatesCount] = useState(0);
  const [duplicatesNorms, setDuplicatesNorms] = useState([]);
  const [remapFor, setRemapFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/biometric/mappings", {
        params: { only_mapped: onlyMapped, ...(q ? { q } : {}) },
      });
      setRows(data.rows || []);
      setDuplicatesCount(data.duplicates_count || 0);
      setDuplicatesNorms(data.duplicates_norms || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [onlyMapped]);

  const filtered = useMemo(() => {
    let r = rows;
    if (onlyIssues) {
      r = r.filter((x) => (x.wrong_attribution_ids?.length > 0)
                             || x.duplicate_bio_id);
    }
    return r;
  }, [rows, onlyIssues]);

  const issuesCount = rows.filter(
    (r) => r.wrong_attribution_ids?.length > 0 || r.duplicate_bio_id,
  ).length;

  return (
    <div className="space-y-4" data-testid="biometric-mappings-page">
      <header className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-slate-900 text-bravura-300 flex items-center justify-center">
          <Fingerprint className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            Phase 5G-A7 · Attendance
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Biometric Mappings
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Har employee ka <b>device user-ID</b> + last-60-day punch attribution.
            Galat mapping yahan se ek click mein theek karein.
          </p>
        </div>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Mapped employees" value={rows.length} tone="blue" />
        <Stat label="Issues found" value={issuesCount}
              tone={issuesCount > 0 ? "rose" : "emerald"} />
        <Stat label="Duplicate IDs" value={duplicatesCount}
              tone={duplicatesCount > 0 ? "amber" : "emerald"}
              note={duplicatesNorms.slice(0, 5).join(", ")} />
        <Stat label="60-day punches" tone="slate"
              value={rows.reduce((s, r) => s + (r.punches_60d || 0), 0)} />
      </div>

      {/* Filters */}
      <div className="bg-white ring-1 ring-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search name / code / device-id / punch-id…"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 data-testid="bm-search" />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <Switch checked={onlyMapped} onCheckedChange={setOnlyMapped}
                  data-testid="bm-toggle-mapped" />
          Only employees with a device ID
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <Switch checked={onlyIssues} onCheckedChange={setOnlyIssues}
                  data-testid="bm-toggle-issues" />
          Only show issues
        </label>
        <Button variant="outline" size="sm" onClick={load} data-testid="bm-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div className="rounded-lg ring-1 ring-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
          No mappings to show with current filters.
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((r) => (
          <MappingRow key={r.id} row={r}
                      onRemap={() => setRemapFor(r)}
                      onChanged={load} />
        ))}
      </div>

      {remapFor && (
        <RemapDialog
          row={remapFor}
          onClose={() => setRemapFor(null)}
          onDone={() => { setRemapFor(null); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, tone, note }) {
  const cls = {
    blue: "text-blue-700 bg-blue-50 ring-blue-200",
    rose: "text-red-700 bg-red-50 ring-red-200",
    amber: "text-amber-700 bg-amber-50 ring-amber-200",
    emerald: "text-emerald-700 bg-emerald-50 ring-emerald-200",
    slate: "text-slate-700 bg-slate-50 ring-slate-200",
  }[tone] || "text-slate-700 bg-slate-50 ring-slate-200";
  return (
    <div className={`ring-1 rounded-lg p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
      {note && <div className="text-[10px] mt-0.5 opacity-80 truncate" title={note}>{note}</div>}
    </div>
  );
}

function MappingRow({ row, onRemap, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hasIssue = (row.wrong_attribution_ids?.length > 0) || row.duplicate_bio_id;

  const clean = async () => {
    if (!window.confirm(
      `Clean wrong attributions for ${row.employee_code} ${row.name}?\n\n` +
      `Yeh sirf un punches ko UNMATCH karega jo iss employee ke saath galti se attached hain ` +
      `(device IDs: ${row.wrong_attribution_ids?.join(", ") || "—"}).\n` +
      `Sahi device ID (${row.biometric_id}) ke punches as-is rahenge.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/biometric/remap", {
        employee_id: row.id,
        new_biometric_id: row.biometric_id,
        retro_relink: true,
        revert_old: true,
      });
      toast.success(
        `Cleaned — ${data.reverted_punches} reverted, ${data.relinked_punches} re-linked`
      );
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const unassign = async () => {
    if (!window.confirm(
      `Unassign biometric ID '${row.biometric_id}' from ${row.employee_code} ${row.name}? ` +
      `Saare historical punches matched=False ho jaayenge.`
    )) return;
    setBusy(true);
    try {
      await api.post("/biometric/unassign", { employee_id: row.id });
      toast.success("Unassigned");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className={`bg-white ring-1 rounded-lg p-3 ${
      hasIssue ? "ring-red-300 bg-red-50/40" : "ring-slate-200"
    }`} data-testid={`bm-row-${row.employee_code}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
        <div className="md:col-span-3">
          <div className="font-semibold text-slate-900 truncate">
            {row.name}
          </div>
          <div className="text-xs text-slate-500">
            <code className="font-mono">{row.employee_code}</code>
            {row.punch_id && (
              <span className="ml-2 text-slate-400">
                · Pay: <code className="font-mono">{row.punch_id}</code>
              </span>
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            Device ID (Bio)
          </div>
          <div className="font-mono text-base font-bold text-slate-900">
            {row.biometric_id || <span className="text-slate-400">—</span>}
          </div>
          {row.duplicate_bio_id && (
            <span className="inline-block text-[10px] bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 mt-0.5">
              DUPLICATE
            </span>
          )}
        </div>

        <div className="md:col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            Punches (60d)
          </div>
          <div className="text-base font-bold text-slate-900">
            {row.punches_60d}
          </div>
          {row.last_punch_date && (
            <div className="text-[10px] text-slate-400">
              Last: {row.last_punch_date}
            </div>
          )}
        </div>

        <div className="md:col-span-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            Attributed Device IDs
          </div>
          <div className="flex flex-wrap gap-1 mt-0.5">
            {(row.distinct_punch_ids_60d || []).map((pid) => {
              const wrong = (row.wrong_attribution_ids || []).includes(pid);
              return (
                <span key={pid}
                      className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                        wrong ? "bg-red-100 text-red-700"
                              : "bg-emerald-100 text-emerald-700"
                      }`}>
                  {pid} {wrong && "⚠"}
                </span>
              );
            })}
            {(row.distinct_punch_ids_60d || []).length === 0 && (
              <span className="text-[10px] text-slate-400">— none —</span>
            )}
          </div>
        </div>

        <div className="md:col-span-2 flex flex-wrap gap-1 justify-end items-start">
          <Button size="sm" variant="outline"
                  onClick={onRemap} disabled={busy}
                  data-testid={`bm-remap-${row.employee_code}`}>
            <Wand2 className="h-3.5 w-3.5 mr-1" /> Re-map
          </Button>
          {row.wrong_attribution_ids?.length > 0 && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={clean} disabled={busy}
                    data-testid={`bm-clean-${row.employee_code}`}>
              <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Clean
            </Button>
          )}
          {row.biometric_id && (
            <Button size="sm" variant="outline" className="text-red-600"
                    onClick={unassign} disabled={busy}
                    data-testid={`bm-unassign-${row.employee_code}`}>
              <Link2Off className="h-3.5 w-3.5" />
            </Button>
          )}
          <button onClick={() => setExpanded((v) => !v)}
                  className="text-xs text-slate-500 hover:text-slate-700">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 mt-2 pt-2 text-xs text-slate-600 grid grid-cols-2 md:grid-cols-4 gap-2">
          <div><b>Status:</b> {row.status || "—"}</div>
          <div><b>Category:</b> {row.category || "—"}</div>
          <div><b>Mapped at:</b> {(row.biometric_mapped_at || "").slice(0,10) || "—"}</div>
          <div><b>norm:</b> <code className="font-mono">{row.biometric_id_norm || "—"}</code></div>
          {hasIssue && (
            <div className="col-span-full text-red-700 bg-red-100/60 rounded p-2 flex gap-1 items-start">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div>
                {row.wrong_attribution_ids?.length > 0 && (
                  <div>
                    <b>Wrong attributions:</b> Iss employee ke 60-day
                    history mein device IDs{" "}
                    <code>{row.wrong_attribution_ids.join(", ")}</code>
                    {" "}ke punches bhi attached hain — jo iska bio-id ({row.biometric_id})
                    nahi hai. <b>Clean</b> button se theek karein.
                  </div>
                )}
                {row.duplicate_bio_id && (
                  <div className="mt-1">
                    <b>Duplicate:</b> Same bio-id (<code>{row.biometric_id_norm}</code>)
                    kisi aur employee pe bhi assigned hai. Search baar pe norm
                    daal ke check karein.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RemapDialog({ row, onClose, onDone }) {
  const [newId, setNewId] = useState(row.biometric_id || "");
  const [retroRelink, setRetroRelink] = useState(true);
  const [revertOld, setRevertOld] = useState(true);
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [clashEmp, setClashEmp] = useState(null);

  const submit = async () => {
    if (!newId.trim()) {
      toast.warning("Naya device ID required"); return;
    }
    setBusy(true);
    setClashEmp(null);
    try {
      const { data } = await api.post("/biometric/remap", {
        employee_id: row.id,
        new_biometric_id: newId.trim(),
        retro_relink: retroRelink,
        revert_old: revertOld,
        force: force,
      });
      toast.success(
        `Done — ${data.reverted_punches} reverted, ${data.relinked_punches} re-linked`
      );
      onDone();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det?.clash_employee) {
        setClashEmp(det.clash_employee);
        toast.warning(det.message || "Conflict");
      } else {
        toast.error(formatApiErrorDetail(det) || "Failed");
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-bravura-700" />
            Re-map biometric ID
          </DialogTitle>
          <DialogDescription>
            <b>{row.employee_code}</b> · {row.name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">
                Current device ID
              </div>
              <div className="font-mono text-base">{row.biometric_id || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">
                Pay punch_id (Paytime)
              </div>
              <div className="font-mono text-base">{row.punch_id || "—"}</div>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">
              New device ID
            </label>
            <Input value={newId} onChange={(e) => setNewId(e.target.value)}
                   placeholder="e.g. 14"
                   data-testid="bm-remap-new-id" autoFocus />
          </div>

          <div className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-3 space-y-2">
            <label className="flex items-start gap-2 text-xs">
              <Switch checked={retroRelink} onCheckedChange={setRetroRelink}
                      data-testid="bm-retro-relink" />
              <div>
                <div className="font-semibold">Retro re-link past punches</div>
                <div className="text-slate-500">
                  Naye ID se related sab purane punches iss employee ko attach
                  ho jaayenge.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-2 text-xs">
              <Switch checked={revertOld} onCheckedChange={setRevertOld}
                      data-testid="bm-revert-old" />
              <div>
                <div className="font-semibold">Clean wrong attributions</div>
                <div className="text-slate-500">
                  Iss employee ke saath galti se attached punches (jo naye ID
                  se nahi match) reset ho jaayenge.
                </div>
              </div>
            </label>
          </div>

          {clashEmp && (
            <div className="rounded-lg bg-amber-50 ring-1 ring-amber-300 p-3 text-xs space-y-1">
              <div className="flex items-center gap-1 font-semibold text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5" />
                Conflict — device ID '{newId}' is already on
                <b>{" "}{clashEmp.employee_code}</b> {clashEmp.name}
              </div>
              <label className="flex items-center gap-2 mt-1">
                <Switch checked={force} onCheckedChange={setForce}
                        data-testid="bm-force" />
                <span>
                  Force — pehle uss employee ka mapping clear karke
                  meri taraf transfer karo (audit logged).
                </span>
              </label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="bm-remap-submit">
            <Link2 className="h-3.5 w-3.5 mr-1" />
            {busy ? "Saving…" : "Save mapping"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
